import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, Clock, Plus, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

export default function AgendaPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRule, setNewRule] = useState({ weekday: 0, start_time: '08:00', end_time: '18:00' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadRules(); }, []);

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/professionals/me/availability/rules`, { headers });
      setRules(res.data || []);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Primeiro, crie seu perfil profissional');
      }
    } finally {
      setLoading(false);
    }
  };

  const addRule = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/professionals/me/availability/rules`, newRule, { headers });
      toast.success('Horário adicionado');
      loadRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar horário');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/api/professionals/me/availability/rules/${ruleId}`, { headers });
      toast.success('Horário removido');
      setRules(prev => prev.filter(r => r.id !== ruleId));
    } catch (err) {
      toast.error('Erro ao remover horário');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="agenda-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Minha Agenda</h1>
          <p className="text-muted-foreground">Defina seus horários disponíveis para atendimento</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadRules}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Add new rule */}
      <Card data-testid="add-rule-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Plus className="h-5 w-5" />
            Adicionar Horário
          </CardTitle>
          <CardDescription>Configure os dias e horários que você atende</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label>Dia da semana</Label>
              <Select value={String(newRule.weekday)} onValueChange={(v) => setNewRule({ ...newRule, weekday: parseInt(v) })}>
                <SelectTrigger data-testid="weekday-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((day, i) => (
                    <SelectItem key={i} value={String(i)}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Início</Label>
              <Select value={newRule.start_time} onValueChange={(v) => setNewRule({ ...newRule, start_time: v })}>
                <SelectTrigger data-testid="start-time-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fim</Label>
              <Select value={newRule.end_time} onValueChange={(v) => setNewRule({ ...newRule, end_time: v })}>
                <SelectTrigger data-testid="end-time-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addRule} disabled={saving} data-testid="add-rule-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Adicionar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current rules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" />
            Horários Configurados
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum horário configurado. Adicione seus horários acima.
            </p>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  data-testid={`rule-${rule.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="min-w-[80px] justify-center">
                      {WEEKDAYS[rule.weekday]}
                    </Badge>
                    <div className="flex items-center gap-1 text-sm">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{rule.start_time}</span>
                      <span className="text-muted-foreground">até</span>
                      <span className="font-medium">{rule.end_time}</span>
                    </div>
                    {rule.enabled ? (
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Ativo</Badge>
                    ) : (
                      <Badge variant="secondary">Inativo</Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteRule(rule.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-rule-${rule.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
